// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import type { GameSummary } from '../../types/game';

interface GameSelectorProps {
  games: GameSummary[];
  selectedGame: string;
  defaultGame: string;
  onSelectGame: (gameId: string) => void;
  loading?: boolean;
}

export function GameSelector({
  games,
  selectedGame,
  defaultGame,
  onSelectGame,
  loading = false,
}: GameSelectorProps) {
  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-4 rounded-lg"
        style={{
          backgroundColor: 'var(--color-surface-hover)',
          border: 'var(--neo-border-thin)',
        }}
      >
        <div
          className="animate-spin h-5 w-5 border-2"
          style={{
            borderColor: 'var(--color-primary)',
            borderTopColor: 'transparent',
          }}
        ></div>
        <span className="ml-2 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          Loading games...
        </span>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <div
        className="p-4 text-sm font-medium"
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-error)',
          border: 'var(--neo-border-thin)',
        }}
      >
        ⚠️ No games available. Please try again later.
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {games.map(game => (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label
          key={game.game_id}
          htmlFor={`game-${game.game_id}`}
          className="block cursor-pointer transition-all"
          style={{
            padding: '1rem',
            border:
              selectedGame === game.game_id ? 'var(--neo-border-medium)' : 'var(--neo-border-thin)',
            backgroundColor:
              selectedGame === game.game_id ? 'var(--color-primary)' : 'var(--color-surface-hover)',
            boxShadow: selectedGame === game.game_id ? 'var(--neo-shadow-md)' : 'none',
          }}
        >
          <div className="flex items-start gap-3">
            <input
              id={`game-${game.game_id}`}
              type="radio"
              name="game"
              value={game.game_id}
              checked={selectedGame === game.game_id}
              onChange={e => onSelectGame(e.target.value)}
              className="mt-1 cursor-pointer"
              style={{ width: '20px', height: '20px' }}
              data-testid={`game-option-${game.game_id}`}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="font-bold"
                  style={{
                    color:
                      selectedGame === game.game_id
                        ? 'var(--color-surface)'
                        : 'var(--color-text-primary)',
                  }}
                >
                  {game.display_name}
                </span>
                {game.game_id === defaultGame && (
                  <span
                    className="text-xs font-bold px-2 py-0.5"
                    style={{
                      backgroundColor: 'var(--color-success)',
                      color: 'var(--color-surface)',
                    }}
                  >
                    ⭐ Default
                  </span>
                )}
              </div>
              <p
                className="text-sm mt-1"
                style={{
                  color:
                    selectedGame === game.game_id
                      ? 'var(--color-surface)'
                      : 'var(--color-text-secondary)',
                }}
              >
                {game.description}
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {(game.tags || []).map(tag => (
                  <span
                    key={tag}
                    className="text-xs font-medium px-2 py-1"
                    style={{
                      backgroundColor:
                        selectedGame === game.game_id ? 'rgba(0,0,0,0.2)' : 'var(--color-surface)',
                      color:
                        selectedGame === game.game_id
                          ? 'var(--color-surface)'
                          : 'var(--color-text-primary)',
                      border: 'var(--neo-border-thin)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div
                className="text-xs font-medium mt-2"
                style={{
                  color:
                    selectedGame === game.game_id
                      ? 'var(--color-surface)'
                      : 'var(--color-text-muted)',
                }}
              >
                👥 {game.min_players}-{game.max_players} players
              </div>
            </div>
          </div>
        </label>
      ))}
    </div>
  );
}
